import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { findRemoteLog } from '../lib/findRemoteLog';
import { readStoredFindGroupId, writeStoredFindGroupId } from '../lib/findSelectedGroupStorage';
import { armUserGroupSelection } from '../config/findSessionGuard';

function resolveInitialGroupId(userId, groups, upcomingGroups) {
  const stored = readStoredFindGroupId(userId);
  const storedGroup = (groups ?? []).find((group) => group.id === stored) ?? null;

  // A stored group whose event is over is last weekend's, not this one's: it used to win here and lock Find onto
  // a dead group while the new one sat unselected (2026-10-04 pre-weekend audit).
  if (storedGroup && !storedGroup.isPast) {
    return { groupId: stored, reason: 'localStorage' };
  }
  const firstUpcoming = upcomingGroups[0]?.id ?? null;
  if (firstUpcoming) {
    return { groupId: firstUpcoming, reason: 'default-first-upcoming' };
  }
  return { groupId: null, reason: 'none' };
}

function logGroupSelectionWrite(source, from, to, extra = {}) {
  if (from === to && source !== 'init') {
    return;
  }
  findRemoteLog(
    'debug',
    'find.group.selected',
    {
      source,
      from: from ?? null,
      to: to ?? null,
      ...extra,
    },
    { skipThrottle: true },
  );
}

/**
 * Persists the last viewed Find group per user.
 * Selection is locked after first resolve — only an explicit commit may change it.
 */
export function useFindSelectedGroup(userId, groups) {
  const lockedGroupIdRef = useRef(null);
  const initDoneForUserRef = useRef(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const upcomingGroups = useMemo(() => (groups ?? []).filter((group) => !group.isPast), [groups]);

  // Resume from localStorage before paint so proximity can start without waiting on groups query.
  useLayoutEffect(() => {
    if (!userId) {
      lockedGroupIdRef.current = null;
      initDoneForUserRef.current = null;
      setSelectedGroupId(null);
      return;
    }

    const stored = readStoredFindGroupId(userId);
    if (stored && lockedGroupIdRef.current !== stored) {
      lockedGroupIdRef.current = stored;
      setSelectedGroupId(stored);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      if (lockedGroupIdRef.current !== null) {
        logGroupSelectionWrite('user-cleared', lockedGroupIdRef.current, null);
      }
      return;
    }

    if (initDoneForUserRef.current === userId) {
      // The default is chosen once per launch, earliest first. When that group's event ends (the group turns past on
      // the next refresh), move on to the next upcoming one: kept open from Friday's show into Saturday's festival,
      // Find stayed on Friday's group and showed nobody (pre-Head Trip audit, 2026-10-07).
      const locked = lockedGroupIdRef.current;
      const over = locked && (groups ?? []).some((g) => g.id === locked && g.isPast);
      if (over && upcomingGroups.length > 0) {
        const next = upcomingGroups[0].id;
        lockedGroupIdRef.current = next;
        setSelectedGroupId(next);
        logGroupSelectionWrite('event-over', locked, next);
      }
      return;
    }

    if (!groups || groups.length === 0) {
      return;
    }

    const { groupId, reason } = resolveInitialGroupId(userId, groups, upcomingGroups);
    initDoneForUserRef.current = userId;
    if (lockedGroupIdRef.current !== groupId) {
      lockedGroupIdRef.current = groupId;
      setSelectedGroupId(groupId);
    }
    logGroupSelectionWrite('init', null, groupId, {
      reason,
      stored: readStoredFindGroupId(userId),
    });
  }, [userId, groups, upcomingGroups]);

  const commitSelectedGroupId = useCallback(
    (next, { source = 'picker' } = {}) => {
      const previous = lockedGroupIdRef.current;
      const value = typeof next === 'function' ? next(previous) : next;
      if (!value || value === previous) {
        findRemoteLog(
          'debug',
          'find.group.commit_skipped',
          { source, groupId: value ?? previous, reason: 'unchanged' },
          { skipThrottle: true },
        );
        return false;
      }

      lockedGroupIdRef.current = value;
      writeStoredFindGroupId(userId, value);
      setSelectedGroupId(value);
      logGroupSelectionWrite(source, previous, value);

      if (source === 'picker') {
        const armed = armUserGroupSelection(value);
        findRemoteLog(
          'debug',
          'find.group.picker_arm',
          { groupId: value, armed },
          { skipThrottle: true },
        );
      }
      return true;
    },
    [userId],
  );

  // After the first resolve the selection is locked to the person's choice - but not to nothing, and not to a
  // group whose event has ended. A group that arrives later (approved into one at the gate) or an event that ends
  // rolls the selection forward to the first upcoming group (2026-10-04 pre-weekend audit).
  useEffect(() => {
    if (!userId || initDoneForUserRef.current !== userId) return;
    const current = (groups ?? []).find((group) => group.id === lockedGroupIdRef.current) ?? null;
    if (current && !current.isPast) return;
    const next = upcomingGroups[0]?.id ?? null;
    if (next && next !== lockedGroupIdRef.current)
      commitSelectedGroupId(next, { source: 'auto-upcoming' });
  }, [userId, groups, upcomingGroups, commitSelectedGroupId]);

  const selectedGroup = useMemo(
    () => (groups ?? []).find((group) => group.id === selectedGroupId) ?? null,
    [groups, selectedGroupId],
  );

  return {
    upcomingGroups,
    selectedGroupId,
    setSelectedGroupId: commitSelectedGroupId,
    selectedGroup,
  };
}
